import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, EnvelopeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import toast from 'react-hot-toast';

interface AddTenantProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: number;
  propertyTitle: string;
  onSuccess: () => void;
}

type TenantFormData = {
  tenantName: string;
  tenantEmail: string;
  tenantPhone?: string; // Optional for invite
  tenancyStartDate: string;
  tenancyEndDate?: string;
  rentAmount: number;
  rentDueDate: number;
  depositAmount?: number;
  rtbRegisteredOn?: string;
};

type SubTenantForm = {
  name: string;
  email: string;
  phone?: string;
};

const AddTenant: React.FC<AddTenantProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyTitle,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [mode, setMode] = useState<'selection' | 'invite' | 'manual'>('selection');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subTenants, setSubTenants] = useState<SubTenantForm[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TenantFormData>();

  const handleManualSubmit = async (data: TenantFormData) => {
    if (!currentUser) return;
    setIsSubmitting(true);

    try {
      const token = await currentUser.getIdToken();
      const cleanedSubTenants = subTenants
        .filter((st) => st.name?.trim() && st.email?.trim())
        .map((st) => ({
          name: st.name.trim(),
          email: st.email.trim(),
          phone: (st.phone || '').trim(),
        }));
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/createManualTenancy`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          propertyId,
          landlordUid: currentUser.uid,
          leaseEndDate: data.tenancyEndDate || null,
          depositAmount: data.depositAmount || 0,
          subTenants: cleanedSubTenants,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create tenancy');
      }

      toast.success('Tenant added successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error adding tenant:', error);
      toast.error(error.message || 'Failed to add tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInviteSubmit = async (data: TenantFormData) => {
    if (!currentUser) return;
    setIsSubmitting(true);

    try {
      const token = await currentUser.getIdToken();
      const cleanedSubTenants = subTenants
        .filter((st) => st.name?.trim() && st.email?.trim())
        .map((st) => ({
          name: st.name.trim(),
          email: st.email.trim(),
          phone: (st.phone || '').trim(),
        }));
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/inviteTenantToProperty`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId,
          tenantEmail: data.tenantEmail,
          tenantName: data.tenantName,
          rentAmount: data.rentAmount,
          rentDueDay: data.rentDueDate,
          leaseStartDate: data.tenancyStartDate,
          leaseEndDate: data.tenancyEndDate || null,
          depositAmount: data.depositAmount || 0,
          rtbRegisteredOn: data.rtbRegisteredOn || null,
          subTenants: cleanedSubTenants,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation');
      }

      toast.success('Invitation sent successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMode('selection');
    reset();
    setSubTenants([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <Dialog.Title className="text-xl font-bold text-gray-900">
                {mode === 'selection' && 'Add Tenant'}
                {mode === 'manual' && 'Manually Add Tenancy'}
                {mode === 'invite' && 'Invite Tenant'}
              </Dialog.Title>
              {propertyTitle && (
                <p className="mt-1 text-sm text-gray-600">
                  For property: <span className="font-semibold">{propertyTitle}</span>
                </p>
              )}
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {mode === 'selection' && (
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => setMode('invite')}
                className="flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
              >
                <div className="p-3 bg-blue-100 rounded-full group-hover:bg-blue-200 mr-4">
                  <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-left flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">Invite by Email</h3>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Send an invitation link to the tenant</p>
                </div>
              </button>

              <button
                onClick={() => setMode('manual')}
                className="flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group"
              >
                <div className="p-3 bg-green-100 rounded-full group-hover:bg-green-200 mr-4">
                  <PencilSquareIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Manually Enter Details</h3>
                  <p className="text-sm text-gray-500">
                    Add tenancy without requiring them to sign up
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Note: You won't be able to communicate with this tenant via the RentSmart
                    platform since they won't receive an email invitation. Email invite is
                    recommended.
                  </p>
                </div>
              </button>
            </div>
          )}

          {(mode === 'manual' || mode === 'invite') && (
            <form
              onSubmit={handleSubmit(mode === 'manual' ? handleManualSubmit : handleInviteSubmit)}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  {...register('tenantName', { required: 'Name is required' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                  placeholder="John Doe"
                />
                {errors.tenantName && (
                  <p className="text-red-500 text-xs mt-1">{errors.tenantName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  {...register('tenantEmail', {
                    required: 'Email is required',
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                  placeholder="john@example.com"
                />
                {errors.tenantEmail && (
                  <p className="text-red-500 text-xs mt-1">{errors.tenantEmail.message}</p>
                )}
              </div>

              {mode === 'manual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    {...register('tenantPhone', {
                      required: 'Phone is required',
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                    placeholder="+353 87 123 4567"
                  />
                  {errors.tenantPhone && (
                    <p className="text-red-500 text-xs mt-1">{errors.tenantPhone.message}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    {...register('tenancyStartDate', {
                      required: 'Start date is required',
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                  />
                  {errors.tenancyStartDate && (
                    <p className="text-red-500 text-xs mt-1">{errors.tenancyStartDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    {...register('tenancyEndDate')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rent Amount (€)</label>
                  <input
                    type="number"
                    {...register('rentAmount', {
                      required: 'Rent amount is required',
                      min: 1,
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                    placeholder="1500"
                  />
                  {errors.rentAmount && (
                    <p className="text-red-500 text-xs mt-1">{errors.rentAmount.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Deposit (€) (Optional)
                  </label>
                  <input
                    type="number"
                    {...register('depositAmount', { min: 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                    placeholder="1500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rent Due Date (Day of Month)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  {...register('rentDueDate', {
                    required: 'Due date is required',
                    min: 1,
                    max: 31,
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                  placeholder="1"
                />
                {errors.rentDueDate && (
                  <p className="text-red-500 text-xs mt-1">{errors.rentDueDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  RTB Registration Date (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Used for compliance reminders regarding annual registration.
                </p>
                <input
                  type="date"
                  {...register('rtbRegisteredOn')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                />
              </div>

              {(mode === 'manual' || mode === 'invite') && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Occupants (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add any additional occupants who live under this tenancy. Name and email are
                    required for each sub-tenant.
                  </p>

                  <div className="space-y-3">
                    {subTenants.length === 0 && (
                      <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded p-3">
                        No sub-tenants added yet.
                      </div>
                    )}

                    {subTenants.map((st, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start mb-4 border-b md:border-b-0 pb-4 md:pb-0 border-gray-100 last:border-0"
                      >
                        {/* NAME FIELD (4 Cols) */}
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            value={st.name}
                            onChange={(e) => {
                              const next = [...subTenants];
                              next[index] = {
                                ...next[index],
                                name: e.target.value,
                              };
                              setSubTenants(next);
                            }}
                            placeholder="Jane Doe"
                            className={`block w-full h-10 rounded-md shadow-sm sm:text-sm px-3 border 
        ${
          !st.name
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
        }`}
                          />
                          {(!st.name || !st.name.trim()) && (
                            <p className="text-red-500 text-xs mt-1 absolute md:static">
                              Name is required
                            </p>
                          )}
                        </div>

                        {/* EMAIL FIELD (4 Cols) */}
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={st.email}
                            onChange={(e) => {
                              const next = [...subTenants];
                              next[index] = {
                                ...next[index],
                                email: e.target.value,
                              };
                              setSubTenants(next);
                            }}
                            placeholder="jane@example.com"
                            className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 border"
                          />
                          {(!st.email || !st.email.trim()) && (
                            <p className="text-red-500 text-xs mt-1 absolute md:static">
                              Email is required
                            </p>
                          )}
                        </div>

                        {/* PHONE FIELD (3 Cols) */}
                        <div className="md:col-span-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Phone
                          </label>
                          <input
                            value={st.phone || ''}
                            onChange={(e) => {
                              const next = [...subTenants];
                              next[index] = {
                                ...next[index],
                                phone: e.target.value,
                              };
                              setSubTenants(next);
                            }}
                            placeholder="+353..."
                            className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 border"
                          />
                        </div>
                        <div className="md:col-span-2 flex md:justify-end lg:col-span-2">
                          <div className="w-full">
                            {/* Spacer for Label height */}
                            <button
                              type="button"
                              onClick={() =>
                                setSubTenants((prev) => prev.filter((_, i) => i !== index))
                              }
                              className="inline-flex items-center justify-center w-full h-10 px-4 text-xs font-semibold text-white transition-colors bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              aria-label={`Remove sub-tenant ${index + 1}`}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div>
                      <button
                        type="button"
                        onClick={() =>
                          setSubTenants((prev) => [...prev, { name: '', email: '', phone: '' }])
                        }
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        + Add sub-tenant
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setMode('selection')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      ((mode === 'manual' || mode === 'invite') &&
                        subTenants.some((st) => !st.name?.trim() || !st.email?.trim()))
                    }
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                      mode === 'invite'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {isSubmitting
                      ? 'Processing...'
                      : mode === 'invite'
                        ? 'Send Invitation'
                        : 'Add Tenancy'}
                  </button>
                </div>
                {(mode === 'manual' || mode === 'invite') &&
                  subTenants.length > 0 &&
                  subTenants.some((st) => !st.name?.trim() || !st.email?.trim()) && (
                    <p className="mt-2 text-xs text-red-600">
                      Please complete all sub-tenant rows: name and email are required.
                    </p>
                  )}
              </div>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AddTenant;
